// @flow

import ReactDOM from 'react-dom';
import { useState } from 'react';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PrsSupport } from '@tectonics/prs/support';
import { type AppValues } from '@tectonics/app-values';
import { type IncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { mapOutgoingPayment, PlaceOrderError, placeOrder, type PlaceOrderErrorPayload } from '@tectonics/prs/outgoing-payment';
import { type UserInputValidationErrors, type UserInputValidator, filterUserInputValidationErrors } from '@tectonics/prs/validation';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { useSubmitTriesCount, useInputErrors, watchSubmitTimeout } from '@tectonics/abw';
import { GooglePayCanceledError, GooglePayError } from '@tectonics/prs/google-pay';
import { FingerprintError } from '@tectonics/prs/fingerprint';
import { handleWarning, handleError } from '@tectonics/alert-handler';
import { ThreeDSecureError } from '@tectonics/prs/threedsecure';
import { getCurrentPaymentMethodOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import { getUserErrorInputField } from '@tectonics/controller/error-messages/get-input-name-from-error';
import { useControllerContext, useLocalTenancyAsTenant } from './context';
import { mapUserInputValidation } from './map-user-input-validation';
import { isUserInputError, isConcurrencyError } from './error-messages';
import { mapUserInputError } from './map-user-input-error';
import { mapServerError } from './map-server-error';
import { sendCheckoutFailAnalytics } from '@tectonics/controller/analytics';

type SetUserInputValidationErrors = ((UserInputValidationErrors) => UserInputValidationErrors) => void;

type UseSubmitArgs = {|
  +lookAndFeel: LookAndFeel,
  +spec: PaymentSpec,
  +prsSupport: PrsSupport,
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: (IncomingPayment) => void,
  +setSubmitError: (?PlaceOrderErrorPayload) => void,
  +appValues: AppValues,
  +setUserInputValidationErrors: SetUserInputValidationErrors,
  +validateUserInput: UserInputValidator,
  +userInputValues: UserInputValues,
|};

export function useSubmit({
  lookAndFeel,
  spec,
  prsSupport,
  incomingPayment,
  setIncomingPayment,
  setSubmitError,
  appValues,
  setUserInputValidationErrors,
  validateUserInput,
  userInputValues,
}: UseSubmitArgs): [boolean, (boolean, boolean) => Promise<bool>] {
  const registerSubmitTry = useSubmitTriesCount(appValues);
  const registerInputErrors = useInputErrors(appValues);
  const [submitting, setSubmitting] = useState(false);
  const { localTenancy } = useControllerContext();
  const tenant = useLocalTenancyAsTenant();

  const handleSubmit = async (hasValidatedLAF: boolean, hasLAFValidationErrors: boolean): Promise<bool> => {
    setSubmitting(false);
    setSubmitError(null);

    const userInputValidationResult = mapUserInputValidation({ spec, prsSupport, validateUserInput, userInputValues, incomingPayment });
    const userInputValidationErrors = filterUserInputValidationErrors(userInputValidationResult);
    const hasValidationErrors = userInputValidationErrors.length > 0;

    registerSubmitTry(hasValidationErrors);
    registerInputErrors({
      userInputValidationErrors,
      hasValidatedLAF,
      hasLAFValidationErrors,
    });

    if (hasValidationErrors) {
      if (ENV_CONSTANTS.ENABLE_OWN_ANALYTICS) {
        try {
          await sendCheckoutFailAnalytics(incomingPayment,
            lookAndFeel,
            userInputValues,
            userInputValidationResult,
            tenant);
        } catch (e) {
          // do nothing since it's not mandatory to have success.
        }
      }

      return false;
    }

    setSubmitting(true);
    try {

      const outgoingPayment = await mapOutgoingPayment(incomingPayment, userInputValues, appValues, localTenancy, lookAndFeel.code);
      return await watchSubmitTimeout(async () => {
        const newIncomingPayment = await placeOrder(outgoingPayment, tenant);
        ReactDOM.unstable_batchedUpdates(() => {
          setIncomingPayment(newIncomingPayment);
          setSubmitting(false);
        });
        return newIncomingPayment.status !== 'OP';
      });
    } catch(error) {
      setSubmitting(false);

      if (error instanceof GooglePayCanceledError) {
        return true;
      }

      if (error instanceof GooglePayError) {
        const code = 'GOOGLE-PAY';
        setSubmitError({
          code,
          message: mapServerError(code, getCountry(incomingPayment)) || error.message,
        });
        return false;
      }

      if (error instanceof FingerprintError) {
        handleWarning(error);
        return false;
      }

      if (error instanceof ThreeDSecureError) {
        const code = 'THREEDSECURE';
        setSubmitError({
          code,
          message: mapServerError(code, getCountry(incomingPayment)) || error.message,
        });
        return false;
      }

      if (!(error instanceof PlaceOrderError)) {
        handleError(error);
        return false;
      }

      if (isConcurrencyError(error.payload.code)) {
        window.location.reload();
        return false;
      }

      if (isUserInputError(error.payload.code)) {
        const mappedUserInputError = mapUserInputError(error.payload.code, incomingPayment.country);
        const [name, message] = mappedUserInputError;

        if (incomingPayment.status === 'OP') {
          const userErrorInputField = getUserErrorInputField(error.payload.code);
          const optionalFields = getCurrentPaymentMethodOptionalFields(incomingPayment, userInputValues, spec);
          if (optionalFields && optionalFields.includes(userErrorInputField)) {
            setSubmitError({
              code: 'CI-000',
              message: mapServerError('CI-000', getCountry(incomingPayment)) || error.payload.message,
            });
            handleError({
              id: 'required-field-set-as-optional',
              payload: {
                message: message || error.payload.message,
                code: error.payload.code,
              },
            });
          }
        }
        setUserInputValidationErrors(results => ({
          ...results,
          [name]: {
            id: 'server-error',
            payload: {
              message: message || error.payload.message,
              code: error.payload.code,
            },
          },
        }));
      } else {
        setSubmitError({
          code: error.payload.code,
          message: mapServerError(error.payload.code, getCountry(incomingPayment)) || error.payload.message,
        });
      }

      return false;
    }
  };

  return [submitting, handleSubmit];
}
